import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';

// ---------------------------------------------------------------------------
// Lazy-loaded pages
// ---------------------------------------------------------------------------

const Landing       = lazy(() => import('./pages/Landing'));
const Login         = lazy(() => import('./pages/Login'));
const Register      = lazy(() => import('./pages/Register'));
const Dashboard     = lazy(() => import('./pages/Dashboard'));
const Profile       = lazy(() => import('./pages/Profile'));
const DeckList      = lazy(() => import('./pages/DeckList'));
const DeckBuilder   = lazy(() => import('./pages/DeckBuilder'));
const GameLobby     = lazy(() => import('./pages/GameLobby'));
const GameBoard     = lazy(() => import('./pages/GameBoard'));
const GameReplay    = lazy(() => import('./pages/GameReplay'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const NotFound      = lazy(() => import('./pages/NotFound'));

// ---------------------------------------------------------------------------
// Loading fallback
// ---------------------------------------------------------------------------

function PageLoader() {
  return (
    <div className="page-loader">
      <div className="loader-spinner" />
      <p>Loading...</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route guards
// ---------------------------------------------------------------------------

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/profile/:id" element={<Profile />} />
            <Route path="/game/:id/replay" element={<GameReplay />} />

            {/* Protected */}
            <Route
              path="/dashboard"
              element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
            />
            <Route
              path="/decks"
              element={<ProtectedRoute><DeckList /></ProtectedRoute>}
            />
            <Route
              path="/decks/new"
              element={<ProtectedRoute><DeckBuilder /></ProtectedRoute>}
            />
            <Route
              path="/decks/:id/edit"
              element={<ProtectedRoute><DeckBuilder /></ProtectedRoute>}
            />
            <Route
              path="/play"
              element={<ProtectedRoute><GameLobby /></ProtectedRoute>}
            />
            <Route
              path="/game/:id"
              element={<ProtectedRoute><GameBoard /></ProtectedRoute>}
            />

            {/* Admin */}
            <Route
              path="/admin"
              element={<AdminRoute><AdminDashboard /></AdminRoute>}
            />

            {/* Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
